import { lumi } from '@/lib/lumi'

export interface ArticlePosterInput {
  title: string
  authorName: string
  dateIso: string
  imageUrl: string
  logoUrl?: string
  siteName?: string
  category?: string
  excerpt?: string
  extraImageUrls?: string[]
}

const W = 1080
const H = 1350
const MARGIN = 28
const PHOTO_H = 780
const PAD = 64
const PROJECT_ID = 'p454626448092758016'
const IMAGE_PROXY = `https://api.lumi.new/v1/functions/${PROJECT_ID}/imageProxy`

type PosterImage = HTMLImageElement | ImageBitmap

function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
) {
  const words = (text || '').trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let current = ''

  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (ctx.measureText(test).width <= maxWidth) {
      current = test
      continue
    }
    if (current) lines.push(current)
    current = word
    if (lines.length === maxLines - 1) break
  }

  if (lines.length < maxLines && current) lines.push(current)

  if (words.length > 0 && lines.length === maxLines) {
    let last = lines[maxLines - 1]
    while (last.length > 3 && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1).trim()
    }
    if (ctx.measureText(last).width > maxWidth || words.join(' ') !== lines.join(' ')) {
      lines[maxLines - 1] = `${last.replace(/[.,;:!?-]+$/, '')}…`
    }
  }

  return lines
}

function formatPosterDate(dateIso: string) {
  const date = new Date(dateIso)
  const valid = !Number.isNaN(date.getTime()) ? date : new Date()
  const weekday = valid.toLocaleDateString('pt-BR', { weekday: 'long' }).split('-')[0].toUpperCase()
  const dayMonth = `${String(valid.getDate()).padStart(2, '0')}/${String(valid.getMonth() + 1).padStart(2, '0')}`
  const year = String(valid.getFullYear())
  const long = valid.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
  return { weekday, dayMonth, year, long }
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

function imageSize(img: PosterImage) {
  return {
    width: 'naturalWidth' in img ? img.naturalWidth || img.width : img.width,
    height: 'naturalHeight' in img ? img.naturalHeight || img.height : img.height,
  }
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: PosterImage,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const size = imageSize(img)
  const ir = size.width / Math.max(size.height, 1)
  const tr = w / h
  let sx = 0
  let sy = 0
  let sw = size.width
  let sh = size.height
  if (ir > tr) {
    sw = size.height * tr
    sx = (size.width - sw) / 2
  } else {
    sh = size.width / tr
    sy = (size.height - sh) / 2
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h)
}

function closeImage(img: PosterImage | null) {
  if (img && 'close' in img && typeof img.close === 'function') {
    img.close()
  }
}

function blobFromBase64(data: string, contentType: string) {
  const binary = atob(data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: contentType || 'image/jpeg' })
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function unwrapProxyPayload(result: unknown) {
  const root = asRecord(result)
  const nested = asRecord(root?.data) || root
  const deeper = asRecord(nested?.data) || nested
  const candidates = [deeper?.data, nested?.data, root?.data, result]
  const data = candidates.find(item => typeof item === 'string' && item.length > 80) as string | undefined
  const contentType = String(deeper?.contentType || nested?.contentType || root?.contentType || 'image/jpeg')
  const error = String(deeper?.error || nested?.error || root?.error || '')
  const success = deeper?.success !== false && nested?.success !== false && root?.success !== false
  return { data: data || '', contentType, error, success }
}

async function imageFromBlob(blob: Blob): Promise<PosterImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob)
    } catch {
      /* fallback HTMLImageElement */
    }
  }
  const objectUrl = URL.createObjectURL(blob)
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('Não foi possível carregar a imagem da matéria.'))
      el.src = objectUrl
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function loadViaSdkProxy(source: string): Promise<PosterImage> {
  const result = await lumi.functions.invoke('imageProxy', {
    method: 'POST',
    body: { url: source },
  })
  const payload = unwrapProxyPayload(result)
  if (!payload.success || !payload.data) {
    throw new Error(payload.error || 'Proxy de imagem indisponível')
  }
  return imageFromBlob(blobFromBase64(payload.data, payload.contentType))
}

async function blobFromUrl(url: string) {
  const res = await fetch(url, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  if (!blob.size) throw new Error('Imagem inválida')
  return blob
}

function proxyUrlFor(source: string) {
  return `${IMAGE_PROXY}?url=${encodeURIComponent(source)}`
}

function uniqueUrls(urls: string[]) {
  const seen = new Set<string>()
  const list: string[] = []
  urls.forEach(raw => {
    const value = (raw || '').trim()
    if (!value || seen.has(value)) return
    seen.add(value)
    list.push(value)
  })
  return list
}

async function loadImage(url: string): Promise<PosterImage> {
  if (!url) throw new Error('Imagem em falta')
  const absolute = new URL(url, window.location.href).toString()
  const errors: unknown[] = []

  try {
    return await loadViaSdkProxy(absolute)
  } catch (err) {
    errors.push(err)
  }

  for (const candidate of [proxyUrlFor(absolute), absolute]) {
    try {
      return await imageFromBlob(await blobFromUrl(candidate))
    } catch (err) {
      errors.push(err)
    }
  }

  const last = errors[errors.length - 1]
  throw last instanceof Error ? last : new Error('Não foi possível carregar a imagem da matéria.')
}

export function canvasToPngBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    try {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Não foi possível gerar o PNG.'))
          return
        }
        resolve(blob)
      }, 'image/png')
    } catch (err) {
      reject(err instanceof Error ? err : new Error('Não foi possível gerar o PNG.'))
    }
  })
}

async function loadOptionalImage(url?: string): Promise<PosterImage | null> {
  if (!url) return null
  try {
    return await loadImage(url)
  } catch (err) {
    console.error('Falha ao carregar imagem do cartaz:', err)
    return null
  }
}

async function loadFirstImage(urls: string[]): Promise<PosterImage | null> {
  for (const url of uniqueUrls(urls)) {
    const image = await loadOptionalImage(url)
    if (image) return image
  }
  return null
}

export function createArticlePosterCanvas() {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  return canvas
}

export function excerptFromArticle(excerpt?: string, content?: string) {
  const raw = (excerpt || content || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return raw.slice(0, 280)
}

export async function drawArticlePoster(
  canvas: HTMLCanvasElement,
  data: ArticlePosterInput,
) {
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas indisponível')

  canvas.width = W
  canvas.height = H

  try {
    await document.fonts.ready
  } catch {
    /* ignora */
  }

  const [photo, logo] = await Promise.all([
    loadFirstImage([data.imageUrl, ...(data.extraImageUrls || [])]),
    loadOptionalImage(data.logoUrl),
  ])
  const date = formatPosterDate(data.dateIso)
  const author = (data.authorName || 'Carlos Felipe').trim()
  const siteName = (data.siteName || 'CARLOS FELIPE').trim()
  const photoX = MARGIN
  const photoY = MARGIN
  const photoW = W - MARGIN * 2

  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, W, H)

  // Foto com cantos arredondados
  ctx.save()
  roundRectPath(ctx, photoX, photoY, photoW, PHOTO_H, 28)
  ctx.clip()
  if (photo) {
    drawCover(ctx, photo, photoX, photoY, photoW, PHOTO_H)
    closeImage(photo)
  } else {
    ctx.fillStyle = '#0f2744'
    ctx.fillRect(photoX, photoY, photoW, PHOTO_H)
  }
  ctx.restore()

  // Selo de data e autor (pequeno) no canto superior esquerdo
  const badgeX = photoX + 20
  const badgeY = photoY + 20
  const badgeH = 84
  const padX = 18
  ctx.font = '700 26px Inter, system-ui, sans-serif'
  const dateW = Math.max(ctx.measureText(date.dayMonth).width, 70)
  ctx.font = '700 20px Inter, system-ui, sans-serif'
  const authorW = Math.min(ctx.measureText(author).width, 300)
  const badgeW = padX + dateW + padX + 1 + padX + authorW + padX
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.35)'
  ctx.shadowBlur = 14
  ctx.shadowOffsetY = 4
  ctx.fillStyle = '#111c2e'
  roundRectPath(ctx, badgeX, badgeY, badgeW, badgeH, 16)
  ctx.fill()
  ctx.restore()

  const dateCenter = badgeX + padX + dateW / 2
  ctx.textAlign = 'center'
  ctx.fillStyle = '#facc15'
  ctx.font = '800 12px Inter, system-ui, sans-serif'
  ctx.fillText(date.weekday, dateCenter, badgeY + 24)
  ctx.fillStyle = '#ffffff'
  ctx.font = '800 26px Inter, system-ui, sans-serif'
  ctx.fillText(date.dayMonth, dateCenter, badgeY + 51)
  ctx.fillStyle = '#facc15'
  ctx.font = '800 14px Inter, system-ui, sans-serif'
  ctx.fillText(date.year, dateCenter, badgeY + 71)

  const dividerX = badgeX + padX + dateW + padX
  ctx.fillStyle = 'rgba(255,255,255,0.35)'
  ctx.fillRect(dividerX, badgeY + 12, 1, badgeH - 24)

  ctx.textAlign = 'left'
  const authorX = dividerX + 1 + padX
  ctx.fillStyle = '#facc15'
  ctx.font = '800 12px Inter, system-ui, sans-serif'
  ctx.fillText('AUTOR', authorX, badgeY + 34)
  ctx.fillStyle = '#ffffff'
  ctx.font = '700 20px Inter, system-ui, sans-serif'
  ctx.fillText(author, authorX, badgeY + 59, authorW)

  // Logo bem pequena no canto inferior direito da foto
  if (logo) {
    const logoH = 56
    const size = imageSize(logo)
    const logoW = Math.min(110, (size.width / Math.max(size.height, 1)) * logoH)
    const lx = photoX + photoW - 20 - logoW
    const ly = photoY + PHOTO_H - 20 - logoH
    ctx.drawImage(logo, lx, ly, logoW, logoH)
    closeImage(logo)
  } else {
    ctx.save()
    ctx.textAlign = 'right'
    ctx.shadowColor = 'rgba(0,0,0,0.6)'
    ctx.shadowBlur = 8
    ctx.fillStyle = '#ffffff'
    ctx.font = '800 18px Inter, system-ui, sans-serif'
    ctx.fillText(siteName, photoX + photoW - 24, photoY + PHOTO_H - 26)
    ctx.restore()
  }

  // Título em maiúsculas
  ctx.textAlign = 'left'
  const textWidth = W - PAD * 2
  let y = photoY + PHOTO_H + 92
  ctx.fillStyle = '#0f172a'
  ctx.font = '800 54px "Playfair Display", Georgia, "Times New Roman", serif'
  const titleLines = wrapLines(ctx, (data.title || '').toUpperCase(), textWidth, 4)
  titleLines.forEach((line, i) => {
    ctx.fillText(line, PAD, y + i * 66)
  })
  y += (titleLines.length - 1) * 66 + 62

  // Linha de autor e data
  ctx.fillStyle = '#6b7280'
  ctx.font = '700 24px Inter, system-ui, sans-serif'
  ctx.fillText(author, PAD, y)
  const authorTextW = ctx.measureText(author).width
  ctx.fillStyle = '#9ca3af'
  ctx.font = '500 24px Inter, system-ui, sans-serif'
  ctx.fillText(date.long, PAD + authorTextW + 36, y)
}

export function downloadCanvasPng(canvas: HTMLCanvasElement, filename: string) {
  canvas.toBlob(blob => {
    if (!blob) return
    downloadPngBlob(blob, filename)
  }, 'image/png')
}

export function downloadPngBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1500)
}

export function slugifyFilename(title: string) {
  return (title || 'materia')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'materia'
}
